import { createSlice, createSelector } from '@reduxjs/toolkit'
import { deleteEnrollment, getEvent, createEvent, updateEvent, deleteEvent, updateRsvpStatus } from '../actions/eventActions'
import { createPairings, updatePairings, deletePairings } from '../actions/pairingsActions'
import { updateSeriesEnrollment } from '../actions/seriesEnrollmentActions'
import { getLevel, yardageByLevel } from '../utility/curriculumHelper'
import moment from 'moment'
import { DATETIME_FORMAT, getMomentInCommunityTimezoneReversed } from '../utility/datesHelper'

const PAIRINGS_INIT_VALUE = {
  id: -1,
  groupsIndex: 0,
  format: '',
  startTime: '',
  interval: 10,
  teeTimeSlots: 1,
  golfersCount: { label: '3', value: 3 },
  startHole: { label: '1', value: 1 },
  holesCount: {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0, 13: 0, 14: 0, 15: 0, 16: 0, 17: 0, 18: 0,
  },
  status: '',
  emailMessage: '',
  pairingGroups: [],
  notifyStudents: true,
}

const initialState = {
  id: -1,
  selectedEvent: {},
  eventLoader: false,
  deleteLoader: false,
  playTypeLoader: true,
  students: [],
  selectedStudents: [],
  courses: [],
  enrollments: [],
  holes: 9,
  playTypeId: -1,
  coachMessage: '',
  description: '',
  eventDate: new Date(),
  location: '',
  name: '',
  isEmail: true,
  uploadPhotoName: '',
  uploadPhotoFile: '',
  uploadPhotoSrc: '',
  indexName: '',
  series: null,
  creatorId: -1,
  isStudentError: false,
  isEventError: false,
  status: null,
  rsvpStatus: null,
  lastRsvp: null,
  pairings: PAIRINGS_INIT_VALUE,
  enableEventCapacity: false,
  hasWaitlistEnabled: false,
  errors: {},
}

const statusPriority = {
  confirmed: 1,
  waitlisted: 2,
  created: 3,
  declined: 4,
}

const sortEnrollments = (a, b) => {
  if (statusPriority[a.status] > statusPriority[b.status]) {
    return 1
  } else if (statusPriority[a.status] < statusPriority[b.status]) {
    return -1
  }

  if (a.pairingGroupId && !b.pairingGroupId) {
    return 1
  } else if (!a.pairingGroupId && b.pairingGroupId) {
    return -1
  } else {
    return 0
  }
}

const mapPairingGroupUsers = (pairingGroup) => {
  const mappedUsers = pairingGroup.users.map(user => ({
    ...user,
    gender: user.gender,
    level: user.currentFormalDivision,
    enrollmentId: user.enrollment.id,
    status: user.enrollment.status,
    pairingGroupPosition: user.enrollment.pairingGroupPosition,
    note: user.enrollment.note,
    age: user.age || '-',
  }))

  const hasPositions = mappedUsers.some(u => u.pairingGroupPosition > 0)
  if (!hasPositions) { return mappedUsers }

  const slots = new Array(pairingGroup.golfersCount).fill(null)
  mappedUsers.forEach(user => {
    const slotIndex = user.pairingGroupPosition - 1
    if (slotIndex >= 0 && slotIndex < slots.length) {
      slots[slotIndex] = user
    }
  })
  return slots
}

const nineHoleEventReducer = createSlice({
  name: 'nineHoleEvent',
  initialState,
  reducers: {
    resetState: () => initialState,
    setField: (state, action) => {
      const { field, value } = action.payload
      state[field] = value
    },

    resetPairingsField: (state) => {
      state.pairings = { ...PAIRINGS_INIT_VALUE, startTime: state.pairings.startTime }
    },

    resetSelectedStudents: state => {
      state.selectedStudents = []
    },

    updateFields: (state, action) => {
      const fields = action.payload
      return {
        ...state,
        ...fields,
      }
    },

    addStudent: (state, action) => {
      const std = action.payload
      state.students.push(std)
    },

    addSelectedStudent: (state, action) => {
      const std = action.payload
      state.selectedStudents.push(std)
    },

    updateStudent: (state, action) => {
      const student = action.payload
      const newStudents = [...state.students]
      const index = newStudents.findIndex(s => s.id === student.id)
      newStudents[index] = student
      state.students = newStudents
    },

    deleteStudent: (state, action) => {
      const id = action.payload
      const index = state.students.findIndex(s => s.id === id)

      state.students.splice(index, 1)
    },

    deleteSelectedStudent: (state, action) => {
      const id = action.payload
      const index = state.selectedStudents.findIndex(s => s.id === id)

      state.selectedStudents.splice(index, 1)
    },

    addCourse: (state, action) => {
      const course = { ...action.payload, selectedPackages: [] }
      state.courses.push(course)
    },
    updateCourse: (state, action) => {
      const course = action.payload
      const newCourses = [...state.courses]
      const index = newCourses.findIndex(c => c.id === course.id)
      newCourses[index] = course
      state.courses = newCourses
    },
    deleteCourse: (state, action) => {
      const courseId = action.payload
      const index = state.courses.findIndex(c => c.id === courseId)

      state.courses.splice(index, 1)
    },
    deletePairingGroup: (state, action) => {
      const pairingGroup = action.payload
      const index = state.pairings.pairingGroups.findIndex(group => group.index === pairingGroup.index)

      if (state.pairings.pairingGroups[index].id) {
        state.pairings.pairingGroups[index]._destroy = true
      } else {
        state.pairings.pairingGroups.splice(index, 1)
      }
    },
    deleteAllPairingGroups: (state, _) => {
      const newGroups = []
      state.pairings.pairingGroups.forEach((group) => {
        if (group.id) {
          newGroups.push({ ...group, _destroy: true })
        }
      })
      state.pairings.pairingGroups = newGroups
    },
  },
  extraReducers: {
    // Delete Enrollment
    [deleteEnrollment.fulfilled]: (state, action) => {
      const filteredStudents = state.students.filter(std => std.id !== action.payload.seriesEnrollment.userId)
      state.students = filteredStudents
      state.rsvpStatus = action.payload.seriesEnrollment.event.rsvpStatus
    },
    [deleteEnrollment.rejected]: (state, action) => {
      state.errors = action.payload
    },

    //UpdateEnrollment
    [updateSeriesEnrollment.fulfilled]: (state, action) => {
      action.payload.seriesEnrollments.forEach((seriesEnrollment) => {
        const student = state.students.find(std => std.id === seriesEnrollment.userId)
        student.status = seriesEnrollment.status
        student.waitlistOrder = seriesEnrollment.waitlistOrder
      })

      state.rsvpStatus = action.payload.rsvpStatus
      state.attendingCount = action.payload.confirmedEnrollmentsCount
      state.responseCount = action.payload.respondedEnrollmentsCount
      state.noResponseCount = state.students.length - action.payload.respondedEnrollmentsCount
      state.notAttendingCount = action.payload.respondedEnrollmentsCount - action.payload.confirmedEnrollmentsCount
      state.spotsAvailable = action.payload.golferLimit
      state.capacityReached = action.payload.capacityReached
    },
    [updateSeriesEnrollment.rejected]: (state, action) => {
      state.errors = action.payload
    },

    //Create Pairings
    [createPairings.fulfilled]: (state, action) => {
      const pairings = action.payload.pairings
      state.pairings = {
        ...PAIRINGS_INIT_VALUE,
        id: pairings.id,
        status: pairings.status,
        format: pairings.format,
        startTime: pairings.startTime,
        interval: pairings.interval,
        teeTimeSlots: pairings.teeTimeSlotsCount,
        holesCount: pairings.shotgunSlotsCount || PAIRINGS_INIT_VALUE.holesCount,
        golfersCount: { value: pairings.golfersCount, label: pairings.golfersCount },
        pairingGroups: pairings ? pairings.pairingGroups.map( (pairingGroup, index) => ({
          ...pairingGroup,
          index: index,
          startHole: { value: pairingGroup.startHole, label: pairingGroup.startHole },
          startTime: moment( pairingGroup.startTime).toDate(),
          playersGroup: { value: pairingGroup.playersGroup, label: pairingGroup.playersGroup },
          users: mapPairingGroupUsers(pairingGroup),
        })) : [],
        groupsIndex: pairings.pairingGroups.length,
        notifyStudents: state.pairings.notifyStudents,
      }
      state.rsvpStatus = pairings.event.rsvpStatus
    },

    //Update Pairings
    [updatePairings.fulfilled]: (state, action) => {
      const pairings = action.payload.pairings
      state.pairings = {
        ...PAIRINGS_INIT_VALUE,
        id: pairings.id,
        status: pairings.status,
        format: pairings.format,
        startTime: pairings.startTime,
        interval: pairings.interval,
        teeTimeSlots: pairings.teeTimeSlotsCount,
        holesCount: pairings.shotgunSlotsCount || PAIRINGS_INIT_VALUE.holesCount,
        golfersCount: { value: pairings.golfersCount, label: pairings.golfersCount },
        pairingGroups: pairings ? pairings.pairingGroups.map( (pairingGroup, index) => ({
          ...pairingGroup,
          index: index,
          startHole: { value: pairingGroup.startHole, label: pairingGroup.startHole },
          startTime: moment( pairingGroup.startTime).toDate(),
          playersGroup: { value: pairingGroup.playersGroup, label: pairingGroup.playersGroup },
          users: mapPairingGroupUsers(pairingGroup),
        })) : [],
        groupsIndex: pairings.pairingGroups.length,
        notifyStudents: state.pairings.notifyStudents,
      }
      state.rsvpStatus = pairings.event.rsvpStatus
    },

    //Delete Pairings
    [deletePairings.fulfilled]: (state, action) => {
      const event = state
      state.pairings = {
        ...PAIRINGS_INIT_VALUE,
        format: event.format,
        timezone: event.timezone,
        startTime: event.eventDate,
        teeTimeSlots: Math.ceil((event.enrollments.length - (event.respondedEnrollmentsCount - event.confirmedEnrollmentsCount))
          / PAIRINGS_INIT_VALUE.golfersCount.value) || 1,
      }
      state.rsvpStatus = action.payload.pairings.event.rsvpStatus
    },

    // Get Event
    [getEvent.fulfilled]: (state, action) => {
      const event = action.payload.event
      const activities = event.activities
      state.id = event.id
      state.name = event.name || `Event #${event.order}`
      state.eventDate = event.eventDate
      state.location = event.location
      state.description = event.description
      state.coachMessage = event.coachMessage || ''
      state.status = event.status
      state.uploadPhotoSrc = event.photo?.largeFileUrl || event.photo?.fileUrl || ''
      state.indexName = `Event #${event.order}`,
      state.nextRsvp = event.nextRsvp
      state.daysUntilEvent = event.daysUntilEvent
      state.attendingCount = event.confirmedEnrollmentsCount
      state.responseCount = event.respondedEnrollmentsCount
      state.noResponseCount = event.enrollments.length - event.respondedEnrollmentsCount
      state.notAttendingCount = event.respondedEnrollmentsCount - event.confirmedEnrollmentsCount
      state.series = event.series
      state.activityEvent = event.activityEvent
      state.format = event.format
      state.isEmail = event.status !== 'completed'
      state.rsvpStatus = event.rsvpStatus
      state.rsvpClosedReason = event.rsvpClosedReason
      state.lastRsvp = event.lastRsvp
      state.enableEventCapacity = !!event.golferLimit
      state.spotsAvailable = event.golferLimit,
      state.hasWaitlistEnabled = event.hasWaitlistEnabled,
      state.spotsLeft = event.spotsLeft,
      state.capacityReached = event.capacityReached,
      state.golferLimit = event.golferLimit,
      state.courses = event.courses,
      state.pairings = event.pairings ? {
        ...PAIRINGS_INIT_VALUE,
        id: event.pairings.id,
        status: event.pairings.status,
        format: event.format,
        startTime: event.pairings.startTime,
        interval: event.pairings.interval,
        teeTimeSlots: event.pairings.teeTimeSlotsCount,
        holesCount: event.pairings.shotgunSlotsCount || PAIRINGS_INIT_VALUE.holesCount,
        golfersCount: { value: event.pairings.golfersCount, label: event.pairings.golfersCount },
        emailMessage: event.pairings.emailMessage,
        pairingGroups: event.pairings ? event.pairings.pairingGroups.map( (pairingGroup, index) => ({
          ...pairingGroup,
          index: index,
          startHole: { value: pairingGroup.startHole, label: pairingGroup.startHole },
          startTime: moment(pairingGroup.startTime).toDate(),
          playersGroup: { value: pairingGroup.playersGroup, label: pairingGroup.playersGroup },
          users: mapPairingGroupUsers(pairingGroup),
        })) : [],
        groupsIndex: event.pairings.pairingGroups.length,
      } : {
        ...PAIRINGS_INIT_VALUE,
        format: event.format,
        timezone: event.timezone,
        startTime: event.eventDate,
        teeTimeSlots: Math.ceil((event.enrollments.length - (event.respondedEnrollmentsCount - event.confirmedEnrollmentsCount))
          / PAIRINGS_INIT_VALUE.golfersCount.value) || 1,
      }

      state.students = []
      if (!event.series) {
        activities?.forEach((activity) => {
          if (activity.type === 'ActivityPlay') {
            const student = activity.user
            if (student) {

              state.students.push({
                ...student,
                milestone: activity.milestone,
                level: getLevel(activity.yardage),
                greens: activity.greens || '',
                putts: activity.putts || '',
                yardage: activity.isFullTee ? 'fullTee' : activity.yardage,
                fullTeeYardage: activity.isFullTee ? activity.yardage : '',
                score: activity.score,
                age: student.age || '-',
              })
            }
          }
        })


      } else {
        event?.enrollments?.sort(sortEnrollments).forEach((e) => {
          const student = e.student
          let addedFrom = null
          if (e.course) {
            if (e.package) {
              addedFrom = { [e.course.id]: { [e.package.id]: { packageName: e.package.name, courseName: e.course.name } } }
            } else {
              addedFrom = { [e.course.id]: e.course.name }
            }
          } else {
            addedFrom = { students: true }
          }

          let studentData = {
            ...student,
            yardage: yardageByLevel[student.currentFormalDivision],
            level: student.currentFormalDivision,
            age: student.age || '-',
            courseName: e.courseName,
            addedFrom: addedFrom,
            status: e.status,
            waitlistOrder: e.waitlistOrder,
            enrollmentId: e.id,
            note: e.note,
            pairingGroupId: e.pairingGroupId,
            pairingGroupOrder: e.pairingGroupOrder,
          }

          const activity = activities.find((a) => a.userId === student.id)
          if (activity) {
            studentData = {
              ...studentData,
              score: activity.score,
              greens: activity.greens || '',
              putts: activity.putts || '',
              yardage: activity.isFullTee ? 'fullTee' : activity.yardage,
              fullTeeYardage: activity.isFullTee ? activity.yardage : '',
              milestone: activity.milestone,
              level: getLevel(activity.yardage),
            }
          }
          state.students.push(studentData)
        })
      }
    },

    // Update RSVP Status
    [updateRsvpStatus.fulfilled]: (state, action) => {
      state.rsvpStatus = action.payload.rsvpStatus
    },

    [getEvent.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [createEvent.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [updateEvent.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [deleteEvent.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [deletePairings.rejected]: (state, action) => {
      state.errors = action.payload
    },
  },
})

export const pairingsFormData = createSelector(
  state => state.nineHoleEvent,
  state => state.user.current_user.communities[0].timezone,
  (fields, timezone) => ({
    eventId: fields.id,
    id: fields.pairings.id,
    format: fields.pairings.format,
    interval: fields.pairings.interval,
    status: fields.pairings.status,
    startTime: fields.pairings.startTime,
    teeTimeSlotsCount: fields.pairings.teeTimeSlots,
    shotgunSlotsCount: JSON.stringify(fields.pairings.holesCount),
    golfersCount: fields.pairings.golfersCount.value,
    emailMessage: fields.pairings.emailMessage?.trim() || 'Hey Everyone, here are your pairings!',
    pairingGroupsAttributes: fields.pairings.pairingGroups.map((pairingGroup) => ({
      ...pairingGroup,
      startTime: pairingGroup.startTime || getMomentInCommunityTimezoneReversed(moment(fields.eventDate), timezone, DATETIME_FORMAT).toDate(),
      startHole: pairingGroup.startHole?.value,
      playersGroup: pairingGroup.playersGroup?.value,
      seriesEnrollmentIds: pairingGroup.users?.map(user => user?.enrollmentId).filter(id => id),
      seriesEnrollmentsAttributes: pairingGroup.users
        ?.map((user, index) => user?.enrollmentId ? { id: user.enrollmentId, pairingGroupPosition: index + 1 } : null)
        .filter(Boolean),
    })),
  })
)

export const {
  resetState,
  setField,
  resetPairingsField,
  updateFields,
  addStudent,
  addSelectedStudent,
  updateStudent,
  deleteStudent,
  deleteSelectedStudent,
  resetSelectedStudents,
  addCourse,
  updateCourse,
  deleteCourse,
  deletePairingGroup,
  deleteAllPairingGroups,
} = nineHoleEventReducer.actions

export const eventFormData = createSelector(
  state => state.nineHoleEvent,
  eventFields => ({
    id: eventFields.id === -1 ? null : eventFields.id,
    name: eventFields.name,
    isEmail: eventFields.isEmail,
    description: eventFields.description,
    location: eventFields.location,
    creatorId: eventFields.creatorId,
    playTypeId: eventFields.playTypeId,
    eventDate: eventFields.eventDate,
    coachMessage: eventFields.coachMessage,
    golferLimit: eventFields.enableEventCapacity ? eventFields.spotsAvailable : '',
    hasWaitlistEnabled: eventFields.hasWaitlistEnabled,
    photoAttributes: eventFields.uploadPhotoFile
      ? { photo: eventFields.uploadPhotoFile, name: eventFields.uploadPhotoFile.name }
      : !eventFields.uploadPhotoSrc ? { _destroy: true } : null,
    activities: eventFields.students?.length > 0
      ? eventFields.students.filter(s => s.status !== 'declined').map((student) => ({
        userId: student.id,
        yardage: student.yardage,
        fullTeeYardage: student.fullTeeYardage || '',
        greens: student.greens || '',
        putts: student.putts || '',
        addedFrom: student.addedFrom,
        score: student.score,
      }))
      : [],
  })
)

export default nineHoleEventReducer.reducer
